import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { CurrencySelectorComponent } from '../../shared/design-system/currency-selector.component';
import { GrowthSegmentSelectorComponent } from '../../shared/design-system/growth-segment-selector.component';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { GrowthCampaign, GrowthMarketingService } from './growth-marketing.service';
import { buildGrowthMarketingChannelOptions } from './growth-channel.constants';
import { combineUtcDateAndTime, splitUtcDateTime } from './growth-datetime.util';
import { CAMPAIGN_STATUS_I18N_KEYS, normalizeCampaignStatus } from './growth-status.util';

interface ChannelMixRow {
  channelKey: string;
  weightPercent: number;
}

interface BudgetRow {
  category: string;
  amount: number;
  currency: string;
}

interface AbVariantRow {
  variantKey: string;
  title: string;
  allocationPercent: number;
}

@Component({
  standalone: true,
  selector: 'ain-growth-marketing-campaign-detail',
  imports: [
    AinPageComponent,
    AinStateComponent,
    AinFormFieldComponent,
    InfoHintComponent,
    GrowthSegmentSelectorComponent,
    CurrencySelectorComponent,
    JalaliDateInputComponent,
    FormsModule,
    ButtonModule,
    SelectModule,
    InputTextModule,
    InputNumberModule,
    RouterLink
  ],
  template: `
    <ain-page [eyebrow]="tr('growth.eyebrow', 'رشد')" [title]="title()" [description]="tr('growth.marketing.campaigns.description', 'جزئیات کمپین بازاریابی را مدیریت کنید.')">
      <div class="toolbar">
        <a pButton routerLink="/growth/marketing/campaigns" severity="secondary" [label]="tr('common.back', 'بازگشت')"></a>
      </div>

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('common.error')" [message]="error() ?? ''" />
      } @else {
        <section class="card">
          <div class="section-title">
            <h3>{{ t('growth.marketing.campaigns.sections.campaignBuilder') }}</h3>
            <ain-info-hint
              [titleKey]="'growth.marketing.campaigns.help.builder.title'"
              [descriptionKey]="'growth.marketing.campaigns.help.builder.description'"
              [bulletKeys]="[
                'growth.marketing.campaigns.help.builder.bullets.channelMix',
                'growth.marketing.campaigns.help.builder.bullets.approvalGate',
                'growth.marketing.campaigns.help.builder.bullets.launch'
              ]" />
          </div>

          <div class="grid">
            <ain-form-field [label]="t('growth.marketing.campaigns.fields.name')"><input pInputText [(ngModel)]="model.name" /></ain-form-field>
            <ain-form-field [label]="t('growth.marketing.campaigns.fields.objective')"><input pInputText [(ngModel)]="model.objective" /></ain-form-field>
            <ain-form-field [label]="t('growth.marketing.campaigns.fields.purpose')">
              <p-select [options]="purposeOptions" optionLabel="label" optionValue="value" [(ngModel)]="model.purposeKey" appendTo="body" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.marketing.campaigns.fields.segment')">
              <ain-growth-segment-selector [(ngModel)]="model.segmentId" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.marketing.campaigns.fields.scheduleDate')"><ain-date-input [(ngModel)]="model.scheduleDateUtc" /></ain-form-field>
            <ain-form-field [label]="t('growth.marketing.campaigns.fields.scheduleTime')"><input type="time" [(ngModel)]="model.scheduleTimeUtc" /></ain-form-field>
          </div>

          <div class="sub-title">{{ t('growth.marketing.campaigns.sections.channelMix') }}</div>
          @for (row of channelMix; track $index) {
            <div class="row-grid">
              <ain-form-field [label]="t('growth.marketing.campaigns.fields.channel')"><p-select [options]="channelOptions" optionLabel="label" optionValue="value" [(ngModel)]="row.channelKey" appendTo="body" /></ain-form-field>
              <ain-form-field [label]="t('growth.marketing.campaigns.fields.weightPercent')"><p-inputNumber [(ngModel)]="row.weightPercent" [min]="0" [max]="100" /></ain-form-field>
              <button pButton type="button" severity="danger" icon="pi pi-trash" (click)="channelMix.splice($index, 1)"></button>
            </div>
          }
          <button pButton type="button" severity="secondary" [label]="t('common.add')" (click)="channelMix.push({ channelKey: 'email', weightPercent: 0 })"></button>

          <div class="sub-title">{{ t('growth.marketing.campaigns.sections.budget') }}</div>
          @for (row of budgetRows; track $index) {
            <div class="row-grid">
              <ain-form-field [label]="t('growth.marketing.campaigns.fields.budgetCategory')"><input pInputText [(ngModel)]="row.category" /></ain-form-field>
              <ain-form-field [label]="t('growth.marketing.campaigns.fields.amount')"><p-inputNumber [(ngModel)]="row.amount" [min]="0" /></ain-form-field>
              <ain-form-field [label]="t('growth.marketing.campaigns.fields.currency')"><ain-currency-selector [(ngModel)]="row.currency" /></ain-form-field>
              <button pButton type="button" severity="danger" icon="pi pi-trash" (click)="budgetRows.splice($index, 1)"></button>
            </div>
          }
          <button pButton type="button" severity="secondary" [label]="t('common.add')" (click)="budgetRows.push({ category: '', amount: 0, currency: 'USD' })"></button>

          <div class="sub-title">{{ t('growth.marketing.campaigns.sections.abVariants') }}</div>
          @for (row of variants; track $index) {
            <div class="row-grid">
              <ain-form-field [label]="t('growth.marketing.campaigns.fields.variantKey')"><input pInputText [(ngModel)]="row.variantKey" /></ain-form-field>
              <ain-form-field [label]="t('growth.marketing.campaigns.fields.variantTitle')"><input pInputText [(ngModel)]="row.title" /></ain-form-field>
              <ain-form-field [label]="t('growth.marketing.campaigns.fields.allocationPercent')"><p-inputNumber [(ngModel)]="row.allocationPercent" [min]="0" [max]="100" /></ain-form-field>
              <button pButton type="button" severity="danger" icon="pi pi-trash" (click)="variants.splice($index, 1)"></button>
            </div>
          }
          <button pButton type="button" severity="secondary" [label]="t('common.add')" (click)="variants.push({ variantKey: '', title: '', allocationPercent: 0 })"></button>

          <div class="actions">
            <button pButton type="button" [label]="isNew() ? t('common.create') : t('common.save')" [disabled]="!canSave()" (click)="save()"></button>
            @if (!isNew() && item()) {
              <span class="status-chip">{{ campaignStatusLabel(item()!.status) }}</span>
            }
          </div>
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { margin-bottom: 12px; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; margin-bottom: 12px; display: grid; gap: 12px; }
    .section-title { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
    .sub-title { font-weight: 600; }
    .grid { display: grid; gap: 12px; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); }
    .row-grid { display: grid; gap: 8px; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); align-items: end; }
    .actions { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .status-chip { border: 1px solid #d1d5db; border-radius: 999px; padding: 0.2rem 0.6rem; font-size: 0.85rem; }
  `]
})
export class GrowthMarketingCampaignDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly service = inject(GrowthMarketingService);
  private readonly i18n = inject(LocalizationService);

  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly isNew = signal(false);
  readonly item = signal<GrowthCampaign | null>(null);

  model = { name: '', objective: '', purposeKey: 'marketing', segmentId: null as string | null, scheduleDateUtc: null as string | null, scheduleTimeUtc: '09:00' };
  channelMix: ChannelMixRow[] = [{ channelKey: 'email', weightPercent: 60 }];
  budgetRows: BudgetRow[] = [{ category: 'default', amount: 1000, currency: 'USD' }];
  variants: AbVariantRow[] = [{ variantKey: 'A', title: 'Primary', allocationPercent: 50 }];

  readonly purposeOptions = [
    { value: 'marketing', label: this.t('growth.marketing.campaigns.purpose.marketing') },
    { value: 'retention', label: this.t('growth.marketing.campaigns.purpose.retention') },
    { value: 'nurture', label: this.t('growth.marketing.campaigns.purpose.nurture') },
    { value: 'growth-loop', label: this.t('growth.marketing.campaigns.purpose.growthLoop') }
  ];
  readonly channelOptions = buildGrowthMarketingChannelOptions(key => this.t(key));

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('campaignId');
    this.isNew.set(!id || id === 'new');
    if (this.isNew()) {
      this.loading.set(false);
      return;
    }
    this.service.listCampaigns().subscribe({
      next: rows => {
        const current = rows.find(x => x.id === id);
        if (!current) {
          this.error.set(this.t('common.notFound'));
          this.loading.set(false);
          return;
        }
        this.bindCampaign(current);
        this.loading.set(false);
      },
      error: () => {
        this.error.set(this.t('common.error'));
        this.loading.set(false);
      }
    });
  }

  title(): string {
    return this.isNew()
      ? this.tr('growth.marketing.campaigns.actions.newCampaign', 'ایجاد کمپین')
      : this.tr('common.edit', 'ویرایش کمپین');
  }

  save(): void {
    const payload = {
      id: this.item()?.id ?? null,
      name: this.model.name,
      objective: this.model.objective,
      segmentId: this.model.segmentId,
      purposeKey: this.model.purposeKey,
      budgetJson: JSON.stringify(this.budgetRows),
      scheduleAtUtc: combineUtcDateAndTime(this.model.scheduleDateUtc, this.model.scheduleTimeUtc),
      channelMixJson: JSON.stringify(this.channelMix),
      abTestConfigJson: JSON.stringify(this.variants),
      simulationRequired: false,
      simulationRef: null
    };
    const request$ = this.isNew() ? this.service.createCampaign(payload) : this.service.updateCampaign(this.item()!.id, payload);
    request$.subscribe(campaign => {
      this.bindCampaign(campaign);
      if (this.isNew()) {
        this.router.navigate(['/growth/marketing/campaigns', campaign.id]);
      }
    });
  }

  canSave(): boolean {
    return this.model.name.trim().length > 0
      && this.model.objective.trim().length > 0
      && this.channelMix.length > 0
      && this.budgetRows.length > 0;
  }

  campaignStatusLabel(status: unknown): string {
    return this.t(CAMPAIGN_STATUS_I18N_KEYS[normalizeCampaignStatus(status)]);
  }

  private bindCampaign(item: GrowthCampaign): void {
    this.isNew.set(false);
    this.item.set(item);
    const scheduleSplit = splitUtcDateTime(item.scheduleAtUtc);
    this.model = {
      name: item.name,
      objective: item.objective,
      purposeKey: item.purposeKey,
      segmentId: item.segmentId,
      scheduleDateUtc: scheduleSplit.dateIso,
      scheduleTimeUtc: scheduleSplit.time
    };
    this.channelMix = this.parseJson(item.channelMixJson, [{ channelKey: 'email', weightPercent: 60 }]);
    this.budgetRows = this.parseJson(item.budgetJson, [{ category: 'default', amount: 1000, currency: 'USD' }]);
    this.variants = this.parseJson(item.abTestConfigJson, [{ variantKey: 'A', title: 'Primary', allocationPercent: 50 }]);
  }

  private parseJson<T>(json: string, fallback: T): T {
    try {
      return JSON.parse(json) as T;
    } catch {
      return fallback;
    }
  }

  t(key: string): string {
    return this.i18n.translate(key);
  }

  tr(key: string, fallback: string): string {
    const translated = this.t(key);
    return translated === key ? fallback : translated;
  }
}
